import {
  AppShell as VjalAppShell,
  Avatar,
  Badge,
  Button,
  Dropdown,
  IconLogout,
  IconUser,
  NavItem,
  NavSection,
  Sidebar,
  StatusDot,
  Topbar
} from "@vjal/ui";
import type { MouseEvent, ReactNode } from "react";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthOtpMutations } from "../hooks/useCoachingQueries";
import { getNavigationRoutes } from "../domain/capabilities";
import type { CoachingRole } from "../domain/roles";
import { CoachingOrgSwitcher } from "./CoachingOrgSwitcher";

export function CoachingAppShell({
  activeRole = "admin",
  activePath = "/dashboard",
  hideNavigation = false,
  isPlatformAdmin = false,
  children
}: {
  activeRole?: CoachingRole;
  activePath?: string;
  hideNavigation?: boolean;
  isPlatformAdmin?: boolean;
  children: ReactNode;
}) {
  const navigate = useNavigate();
  const { logout } = useAuthOtpMutations();
  const navigationRoutes = getNavigationRoutes(activeRole, isPlatformAdmin);
  const roleLabel = hideNavigation ? "auth" : activeRole;
  const compactRoleLabel = hideNavigation ? "auth" : compactRoleToken(activeRole);

  // Logout clears the platform session cookie (the auth-service `logout`
  // mutation) and removes the whole coaching query cache (`onSuccess` in
  // useAuthOtpMutations), so no signed-in data survives the sign-out. We route
  // to /login optimistically; the cleared profile cache also re-derives the
  // route screen to the login surface. The control lives in the Topbar so it is
  // reachable on mobile (the topbar is always visible) without opening the rail.
  const handleLogout = () => {
    logout.mutate(undefined, {
      onSettled: () => navigate("/login", { replace: true })
    });
  };

  // @vjal/ui AppShell keeps scroll on #main but tabIndex=-1; make the managed
  // scroll region keyboard-focusable for axe scrollable-region-focusable (tools +
  // every inherited screen). Inner regions that already set tabIndex=0 stay valid.
  useEffect(() => {
    const main = document.getElementById("main");
    if (!main) return;
    main.tabIndex = 0;
    if (!main.getAttribute("aria-label")) {
      main.setAttribute("aria-label", "Main content");
    }
  }, []);

  return (
    <VjalAppShell
      children={<div className="min-w-0 max-w-full">{children}</div> as never}
      // With navigation hidden the rail column collapses to 0 so <main> spans the
      // full width. (Overriding lg:grid-cols-* via className loses the CSS-order
      // race against the package class and squeezed auth surfaces into the rail.)
      railWidth={hideNavigation ? 0 : 276}
      topbar={
        <div className="min-w-0">
          <Topbar
            className={`min-w-0 gap-2 px-3 sm:gap-4 sm:px-5 ${hideNavigation ? "[&>button:first-child]:hidden" : ""}`}
            brand={
              <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
                <Avatar alt="Coaching Dashboard" initials="CD" />
                <div className="min-w-max sm:min-w-0">
                  <p className="m-0 whitespace-nowrap text-sm font-semibold text-white">
                    Coaching Dashboard
                  </p>
                  <p className="m-0 hidden min-w-0 items-center gap-2 text-xs text-white/65 sm:flex">
                    <StatusDot tone="ok" label="Operational workspace" />
                    <span>Operational workspace</span>
                    <span aria-hidden="true">/</span>
                    Coaching operations
                  </p>
                </div>
              </div>
            }
            actions={
              <div className="flex min-w-0 shrink-0 items-center gap-2 sm:gap-3">
                {hideNavigation ? null : <CoachingOrgSwitcher />}
                {hideNavigation ? null : (
                  <Dropdown
                    className="sm:hidden [&_[data-dropdown-trigger]]:rounded-lg [&_[data-dropdown-trigger]]:focus-visible:outline-none [&_[data-dropdown-trigger]]:focus-visible:shadow-focus-dark"
                    menuWidth="w-44"
                    side="bottom-end"
                    trigger={
                      <span className="inline-flex min-h-tap min-w-tap items-center justify-center rounded-lg text-white/75 hover:bg-white/10 hover:text-white">
                        <IconUser size={18} aria-hidden="true" />
                        <span className="sr-only">Account menu</span>
                      </span>
                    }
                    items={[
                      {
                        id: "role",
                        label: <span className="font-medium">{roleLabel}</span>,
                        icon: <IconUser size={16} aria-hidden="true" />,
                        disabled: true
                      },
                      {
                        id: "logout",
                        label: logout.isPending ? "Logging out..." : "Logout",
                        icon: <IconLogout size={16} aria-hidden="true" />,
                        onSelect: handleLogout,
                        disabled: logout.isPending,
                        danger: true
                      }
                    ]}
                  />
                )}
                <div className="hidden min-w-0 shrink-0 items-center gap-2 sm:flex sm:gap-3">
                  <Badge variant="on-ink">{renderResponsiveRoleLabel(compactRoleLabel, roleLabel)}</Badge>
                  {hideNavigation ? null : (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="shrink-0 text-white hover:bg-white/10 focus-visible:shadow-focus-dark"
                      loading={logout.isPending}
                      onClick={handleLogout}
                      aria-label="Logout"
                      leftIcon={<IconLogout size={16} aria-hidden="true" />}
                    >
                      Logout
                    </Button>
                  )}
                </div>
              </div>
            }
          />
        </div>
      }
      sidebar={
        hideNavigation ? undefined :
        <Sidebar
          ariaLabel="Coaching navigation"
          className="focus-visible:outline-none focus-visible:shadow-focus-dark"
          tabIndex={0}
        >
          <div className="px-3 py-3">
            <p className="m-0 text-xs font-semibold uppercase tracking-eyebrow text-white/55">Workspace</p>
            <p className="m-0 mt-1 text-sm text-white">Your available sections.</p>
          </div>
          <NavSection>Workspace</NavSection>
          {navigationRoutes.map((route) => (
            <NavItem
              active={route.path === activePath}
              className="focus-visible:shadow-focus-dark"
              href={route.path}
              key={route.id}
              onClick={(event) => {
                if (!isPlainLeftClick(event)) return;
                event.preventDefault();
                navigate(route.path);
              }}
            >
              {route.label}
            </NavItem>
          ))}
          <div className="mt-auto border-t border-white/10 px-3 pb-2 pt-3">
            <a
              className="text-xs text-white/55 underline underline-offset-2 hover:text-white focus-visible:outline-none focus-visible:shadow-focus-dark"
              href="/user-manual/"
              rel="noopener noreferrer"
              target="_blank"
            >
              User manual
            </a>
          </div>
        </Sidebar>
      }
    />
  );
}

function isPlainLeftClick(event: MouseEvent<HTMLAnchorElement>) {
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
}

function renderResponsiveRoleLabel(compactLabel: string, fullLabel: string) {
  if (compactLabel === fullLabel) return fullLabel;

  return (
    <>
      <span className="sm:hidden">{compactLabel}</span>
      <span className="hidden sm:inline">{fullLabel}</span>
    </>
  );
}

function compactRoleToken(role: CoachingRole) {
  const labels: Record<CoachingRole, string> = {
    super_admin: "SA",
    admin: "Admin",
    coach: "Coach",
    company_admin: "CA",
    participant: "Learner",
    cohort_lead: "Lead"
  };

  return labels[role];
}
